import React, { createContext, useContext, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { isLitresError } from "@litres/errors";
import { captchaError } from "./errors";

const RECAPTCHA_SITE_KEY = "6Lcg05ooAAAAAOEqySkltXch1UOeWXXuuEY9QVz1";

const captchaContext = createContext<string | null>(null);

export function useCaptchaContext() {
  const context = useContext(captchaContext);

  if (!context) {
    throw captchaError(
      "FrontendLogicError",
      "useCaptchaContext must be used within a CaptchaContext provider"
    );
  }

  return context;
}

interface CaptchaProviderProps {
  children: React.ReactNode;
}

export const CaptchaProvider: React.FC<CaptchaProviderProps> = ({
  children,
}) => {
  const handleBoundaryError = (error: Error) => {
    if (isLitresError(error) && error.feature === "CaptchaError") {
      captchaError("ExpectedBusinessFlowError", "ReCaptcha error boundary", {
        originalError: error,
      }).emit();
    }
  };

  useEffect(() => {
    const scriptId = "google-recaptcha-script";
    const scriptSrc = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;

    if (document.getElementById(scriptId)) {
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;

    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <ErrorBoundary fallback={null} onError={handleBoundaryError}>
      <captchaContext.Provider value={RECAPTCHA_SITE_KEY}>
        {children}
      </captchaContext.Provider>
    </ErrorBoundary>
  );
};
